import { Bell, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useNotifications } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export const NotificationBell = () => {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = async (notification: any) => {
    await markAsRead(notification.id);

    // Navigate based on notification type and reference
    if (notification.reference_type === 'vendor_application' && notification.type === 'vendor_application') {
      // For organizers - reference_id is vendor_application id, need to get event_id
      try {
        const { data: application } = await supabase
          .from('vendor_applications')
          .select('event_id')
          .eq('id', notification.reference_id)
          .single();
        
        if (application?.event_id) {
          navigate(`/event/${application.event_id}/manage`);
        }
      } catch (error) {
        console.error('Error fetching vendor application:', error);
      }
    } else if (notification.reference_type === 'sponsor_application' && notification.type === 'sponsor_application') {
      // For organizers - reference_id is sponsor_application id, need to get event_id
      try {
        const { data: application } = await supabase
          .from('sponsor_applications')
          .select('event_id')
          .eq('id', notification.reference_id)
          .single();
        
        if (application?.event_id) {
          navigate(`/event/${application.event_id}/manage`);
        }
      } catch (error) {
        console.error('Error fetching sponsor application:', error);
      }
    } else if (notification.reference_type === 'vendor_application' && (notification.type === 'status_change' || notification.type === 'approval')) {
      // For vendors - application approved, go to event to pay
      try {
        const { data: application } = await supabase
          .from('vendor_applications')
          .select('event_id')
          .eq('id', notification.reference_id)
          .single();
        
        if (application?.event_id) {
          navigate(`/event/${application.event_id}`);
        } else {
          navigate('/profile');
        }
      } catch (error) {
        console.error('Error fetching vendor application:', error);
        navigate('/profile');
      }
    } else if (notification.reference_type === 'vendor_application' && notification.type === 'invoice') {
      // For vendors with invoice - go to event to pay
      try {
        const { data: application } = await supabase
          .from('vendor_applications')
          .select('event_id')
          .eq('id', notification.reference_id)
          .single();
        
        if (application?.event_id) {
          navigate(`/event/${application.event_id}`);
        } else {
          navigate('/profile');
        }
      } catch (error) {
        console.error('Error fetching vendor application:', error);
        navigate('/profile');
      }
    } else if (notification.type === 'favorite_vendor_event' && notification.reference_id) {
      // For users following vendors - go to event details
      navigate(`/event/${notification.reference_id}`);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'vendor_application':
      case 'sponsor_application':
        return '📝';
      case 'status_change':
        return '🔄';
      case 'invoice':
        return '💰';
      case 'favorite_vendor_event':
        return '⭐';
      default:
        return '🔔';
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              variant="destructive"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-8 text-xs"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${
                    !notification.read ? 'bg-muted/30' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <span className="text-2xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm line-clamp-1">
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
